import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { LOGIN } from "../../Constants/apiRoutes";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import { HiMail, HiLockClosed } from "react-icons/hi";
import DealVisorLogo from "../../assets/Images/Deal_visor.png";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import md5 from "md5";
import { toast, ToastContainer } from "react-toastify";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const features = [
    {
      icon: "📄",
      title: "Document Upload",
      description: "Securely upload and manage your documents",
    },
    {
      icon: "✍️",
      title: "Digital Signatures",
      description: "Sign documents electronically",
    },
    {
      icon: "👥",
      title: "Collaboration",
      description: "Review and approve documents together",
    },
    {
      icon: "🔒",
      title: "Secure Storage",
      description: "Keep your documents safe and organized",
    },
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const hashedPassword = md5(password);

      const response = await fetch(LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Email: email, Password: hashedPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        const {
          token,
          RoleID,
          DepartmentIDs,
          UserID,
          TenantID,
          PermissionIDs,
        } = data;

        localStorage.setItem("UserID", UserID);
        localStorage.setItem("TenantID", TenantID);
        localStorage.setItem("DepartmentIDs", DepartmentIDs);
        localStorage.setItem("PermissionIDs", JSON.stringify(PermissionIDs));
        await login(token, RoleID, UserID, DepartmentIDs);

        const storedUserID = localStorage.getItem("UserID");
        const storedTenantID = localStorage.getItem("TenantID");
        const storedDepartments = localStorage.getItem("DepartmentIDs");

        if (
          storedUserID !== null &&
          storedUserID !== "undefined" &&
          storedTenantID !== null &&
          storedTenantID !== "undefined" &&
          storedDepartments !== null &&
          storedDepartments !== "undefined"
        ) {
          const checkUserData = () => {
            const userData = localStorage.getItem("userData");

            if (userData) {
              const redirectPath = sessionStorage.getItem("redirectAfterLogin");
              sessionStorage.removeItem("redirectAfterLogin");
              navigate(redirectPath || "/dashboard");
            } else {
              setTimeout(checkUserData, 1000);
            }
          };

          checkUserData();
        } else {
          toast?.error?.(
            "Login failed. Required Details Missing in Local Storage."
          );
        }
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } catch (error) {
      setError("An error occurred during login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex overflow-hidden">
      {/* Background Pattern with Gradient */}{" "}
      {isLoading && <LoadingAnimation />}
      <ToastContainer />
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white opacity-80" />

        {/* Animation Layer - Using GIF */}
        <div className="absolute inset-0 z-10 opacity-75 align-center">
          <DotLottieReact
            src="https://lottie.host/79fbfbdd-bf0f-4478-9136-b101cd217fe8/T7NlAlJBhK.lottie"
            loop={true}
            autoplay={true}
            className="absolute inset-0 h-full w-full object-contain shadow-sm"
          />
        </div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-pattern opacity-10" />
      </div>
      {/* Content */}
      <div className="relative z-20 flex w-full">
        {/* Left Side - Visible on tablet and desktop */}
        <div className="flex-1 p-8 lg:p-12 hidden sm:block">
          <div className="flex items-center gap-10">
            {/* Logo */}
            <img
              src={DealVisorLogo}
              alt="DealVisorLogo"
              className="h-28 w-auto"
            />

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-[#8B4513]">
              Login into
              <br />
              your account
            </h1>
          </div>
          <div className="text-[#301607] max-w-xl mt-10">
            {/* Features Grid - Updated background and text colors */}
            <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 mt-40">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 bg-[#8B4513] rounded-lg p-4 transition-all duration-300 backdrop-blur-sm 
      border border-[#8B4513]" // Add border here
                >
                  <div className="text-white text-2xl shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-white text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
          <div className="bg-[#8B4513] rounded-3xl p-6 sm:p-8 w-full max-w-[400px] shadow-xl backdrop-blur-lg">
            <form onSubmit={handleLogin} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label className="text-[#fff] text-sm sm:block hidden">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                    placeholder="admin@example.com"
                  />
                  <HiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-[#fff] text-sm sm:block">Password</label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 pl-10 bg-[#F1F5F9] rounded-lg text-gray-700"
                    placeholder="••••••••"
                  />
                  <HiLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-[#fff] text-sm hover:underline"
                >
                  Forgot your Password?
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <p className="text-white text-sm text-center">{error}</p>
              )}

              {/* Login Button */}
              <button
                type="submit"
                className="w-full bg-[#fff] text-[#8B4513] py-3 rounded-lg font-medium transition-colors"
                disabled={isLoading}
              >
                Login
              </button>
              <p className="text-white text-center">
                Don't have an account?{" "}
                <a
                  onClick={() => navigate("/signup")}
                  className="underline cursor-pointer"
                >
                  Sign up
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
